// Deterministic node-subtype derivation. Subtypes are NOT persisted — every
// call to `resolveNodeSubtype(template, node, mapSeed)` reproduces the same
// answer for the same arguments, so the run state stays free of subtype
// bookkeeping while the UI (and Step 10b-2's sim driver) get a stable result.
//
// Seed derivation is a fixed Knuth-multiplier xor fold (no splitmix branch,
// no custom hash). The standard 12-floor layout has at most ~25 nodes, so
// the small chance of seed collision is offset by the broad downstream draws
// `Rng.fromSeed` produces; distribution health is pinned by tests.
//
// Phase 3 Step 10b-1.

import {
  DEFAULT_CAMP_SUBMENU,
  DEFAULT_EVENT_SUBTYPE,
  Rng,
  SUBTYPE_TO_PARENT,
  findSubtypeRollTier,
  type CampSubmenuRoll,
  type DungeonNodeSubtype,
  type DungeonNodeType,
  type DungeonTemplate,
  type EventSubtypeRoll,
  type GeneratedNode,
  type NodeCoord,
} from '@renavora/shared';

/**
 * `NodeCoord` convention (matches 10a-1's `applyAdvance` / `formatNodeKey`):
 *   - `coord[0]` = floorIndex, **1-based** (1..12, matches GeneratedNode.floorIndex)
 *   - `coord[1]` = nodeIndex, **0-based** (0..nodeCount-1)
 *
 * Both are mixed into the seed via fixed Knuth-multiplier xor fold so that
 * adjacent coords land far apart in the 32-bit unsigned seed space.
 */
export function subtypeSeed(mapSeed: number, coord: NodeCoord): number {
  const [floor, index] = coord;
  return (
    (mapSeed ^ Math.imul(floor, 0x9e3779b9) ^ Math.imul(index, 0x85ebca6b)) >>> 0
  );
}

/** Thin convenience wrapper. Exported so tests can pull a fresh RNG too. */
export function nodeSubtypeRng(mapSeed: number, coord: NodeCoord): Rng {
  return Rng.fromSeed(subtypeSeed(mapSeed, coord));
}

/**
 * Pick a key from a weighted distribution. Weights must sum to ~1; trailing
 * floating-point slack falls through to the last key. Mirrors the private
 * `pickWeighted` inside `@renavora/shared/dungeon.ts` — re-stated here so
 * we don't have to widen the shared surface.
 */
function pickWeighted<T extends string>(
  rng: Rng,
  weights: Readonly<Record<T, number>>,
  keys: readonly T[],
): T {
  const roll = rng.nextFloat();
  let acc = 0;
  for (const k of keys) {
    acc += weights[k];
    if (roll < acc) return k;
  }
  return keys[keys.length - 1]!;
}

const BATTLE_KEYS = ['light', 'normal', 'heavy'] as const;
const ELITE_KEYS = ['elite', 'midboss', 'rarepack'] as const;
const EVENT_KEYS = ['text', 'treasure', 'ambush'] as const;
const CAMP_KEYS = ['rest', 'boon', 'shop'] as const;

export function rollBattleSubtype(
  template: DungeonTemplate,
  rng: Rng,
): 'light' | 'normal' | 'heavy' {
  const tier = findSubtypeRollTier(template.subtypeRolls, template.tier);
  return pickWeighted(rng, tier.battle, BATTLE_KEYS);
}

export function rollEliteSubtype(
  template: DungeonTemplate,
  rng: Rng,
): 'elite' | 'midboss' | 'rarepack' {
  const tier = findSubtypeRollTier(template.subtypeRolls, template.tier);
  return pickWeighted(rng, tier.elite, ELITE_KEYS);
}

export function rollEventSubtype(
  template: DungeonTemplate,
  rng: Rng,
): 'text' | 'treasure' | 'ambush' {
  const roll: EventSubtypeRoll = template.eventSubtype ?? DEFAULT_EVENT_SUBTYPE;
  return pickWeighted(rng, roll, EVENT_KEYS);
}

export function rollCampSubtype(
  template: DungeonTemplate,
  rng: Rng,
): 'rest' | 'boon' | 'shop' {
  const roll: CampSubmenuRoll = template.campSubmenu ?? DEFAULT_CAMP_SUBMENU;
  return pickWeighted(rng, roll, CAMP_KEYS);
}

/**
 * Resolve the subtype for a generated node.
 *
 * Returns `null` only for `start` nodes (no subtype concept). Boss nodes
 * always carry `node.subtype === 'boss'` via the layout, so we return that
 * verbatim. Camp/event layout-fixed nodes (Floor 6 shop, Floor 11 rest)
 * carry their subtype too; we respect any pre-set `node.subtype` over the
 * roll so the generator's intent never gets overwritten.
 */
export function resolveNodeSubtype(
  template: DungeonTemplate,
  node: GeneratedNode,
  mapSeed: number,
): DungeonNodeSubtype | null {
  if (node.type === 'start') return null;
  if (node.subtype !== undefined) {
    // Layout-fixed subtype (boss, Floor 6 shop, Floor 11 rest, etc.).
    return node.subtype;
  }
  const rng = nodeSubtypeRng(mapSeed, [node.floorIndex, node.nodeIndex]);
  const parent: DungeonNodeType = node.type;
  switch (parent) {
    case 'battle':
      return rollBattleSubtype(template, rng);
    case 'elite':
      return rollEliteSubtype(template, rng);
    case 'event':
      return rollEventSubtype(template, rng);
    case 'camp':
      return rollCampSubtype(template, rng);
    case 'boss':
      // boss layout-fixed nodes already returned above; defensive fallback
      // in case a future generator emits an un-fixed boss node.
      return 'boss';
    default: {
      // Exhaustiveness check — TS will complain if a new parent type lands
      // without us updating this switch.
      const _exhaustive: never = parent;
      void _exhaustive;
      // Belt-and-braces: the SUBTYPE_TO_PARENT table covers every known
      // subtype, so a never branch here means we missed updating this fn.
      void SUBTYPE_TO_PARENT;
      return null;
    }
  }
}
